import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, OnDestroy, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CUSTOM_MASK, emailPattern } from '@helpers/custom-validator';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { IDefaultSelect } from '@interfaces/default-select';
import { enumOrganizationRole } from '@app/data-layer/enums/organizations';
import { TranslateService } from '@ngx-translate/core';
import { Observable, ReplaySubject, forkJoin } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { OrganizationInviteMemberModel, OrganizationMemberModel } from '@app/data-layer/models/organizations';
import { OrganizationUpdateMemberModel } from '@app/data-layer/models/organizations/organization-update-member-model';
import { CompanyModel } from '@app/data-layer/models/companies';
import { AppService } from '@app/app.service';

interface UpdateInviteSourceType {
  data: any;
  member: OrganizationMemberModel;
}

@Component({
  selector: 'organization-member-invite-form',
  templateUrl: './organization-member-invite-form.component.html',
  styleUrls: ['./organization-member-invite-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationMemberInviteFormComponent implements OnInit, OnDestroy {
  public formGroup: UntypedFormGroup;
  public isEditView: boolean;
  public currentMember: OrganizationMemberModel;
  public alreadyAcceptedMember: OrganizationMemberModel;
  public acceptedMembers: OrganizationMemberModel[];
  public selectedCompaniesIds: number[] = [];
  public companies: CompanyModel[] = [];
  public roles: IDefaultSelect[];
  public enumOrganizationRole: typeof enumOrganizationRole = enumOrganizationRole;
  public CUSTOM_MASK = CUSTOM_MASK;
  public errors: { [name: string]: IErrorForm } = {
    email: {
      pattern: 'FORM_ERROR.LOGIN_EMAIL',
      maxlength: 'FORM_ERROR.MAX_LENGTH',
    },
  };

  @Output() public sendInviteSource: EventEmitter<any> = new EventEmitter<any>();
  @Output() public updateInviteSource: EventEmitter<UpdateInviteSourceType> = new EventEmitter<UpdateInviteSourceType>();

  private destroy$: ReplaySubject<boolean> = new ReplaySubject();

  constructor(private appService: AppService, private translateService: TranslateService) {}

  public ngOnInit(): void {
    this.isEditView = !!this.currentMember;
    this.getRoleNames()
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        this.roles = items.map((item) => {
          return {
            id: item.id,
            value: item.label.toLowerCase(),
            label: item.label,
          };
        });
      });

    this.createForm();

    //#10917 - fix handles member update instead of re-invite if already exists
    this.formGroup
      .get('email')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((email) => {
        this.alreadyAcceptedMember = this.getAlreadyAcceptedMember(email);
        if (!!this.alreadyAcceptedMember) {
          this.appService.setSidebarOptions({ sidebarHeaderText: 'BUTTON.EDIT_PERMISSIONS' });
          this.selectedCompaniesIds = this.getOrganizationMembersCompanyIds(this.alreadyAcceptedMember.user.userId);
          this.formGroup.get('companies').reset();
          this.selectedCompaniesIds.forEach((companyId) => {
            const companyControl = this.getCompanyControlById(companyId) as UntypedFormControl;
            companyControl.patchValue(true);
          });
          this.formGroup.get('role').patchValue(String(this.alreadyAcceptedMember.role));
        } else {
          this.appService.setSidebarOptions({ sidebarHeaderText: 'LABELS.INVITE_MEMBERS' });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public sendInvite(): void {
    if (!this.formGroup.valid) {
      this.formGroup.updateValueAndValidity();
      return;
    }

    const companies = this.isAdmin ? this.companies : this.companies.filter((company) => this.getCompanyControlById(company.id).value);
    const companyIds = companies.map((company) => company.id);

    let data: OrganizationInviteMemberModel | OrganizationUpdateMemberModel;
    if (this.isEditView || (this.isMemberAlreadyAccepted && !!this.alreadyAcceptedMember)) {
      data = {
        role: Number(this.formGroup.get('role').value),
        companies: [...companyIds],
      };
      this.updateInviteSource.emit({ data, member: this.currentMember || this.alreadyAcceptedMember });
    } else {
      data = {
        role: Number(this.formGroup.get('role').value),
        email: this.formGroup.get('email').value,
        companies: [...companyIds],
      };
      this.sendInviteSource.emit(data);
    }
  }

  public get isAnyCompanySelected(): boolean {
    return this.companies.filter((company, i) => this.getCompanyControlById(company.id).value).length > 0;
  }

  public get isAdmin(): boolean {
    return Number(this.formGroup.get('role').value) === enumOrganizationRole.ADMIN;
  }

  public get roleHelperText(): string {
    return this.isAdmin ? 'FORM_HELPER.MEMBER_ROLE' : '';
  }

  public get isMemberAlreadyAccepted(): boolean {
    return this.acceptedMembers.some((member) => member?.user.email.toLowerCase() === this.formGroup.get('email').value.toLowerCase());
  }

  public getAlreadyAcceptedMember(email: string): OrganizationMemberModel {
    return this.acceptedMembers.find((member) => member?.user.email.toLowerCase() === email.toLowerCase());
  }

  public getOrganizationMembersCompanyIds(userId: string): number[] {
    const memberDuplicates = this.acceptedMembers?.filter((m) => m.user.userId === userId);
    return memberDuplicates.map((member) => member.company.id);
  }

  public getCompanyControlById(companyId: number): any {
    const companiesFormArray = (this.formGroup.controls.companies as UntypedFormArray).controls;
    return companiesFormArray.find((control) => control.get(String(companyId)))?.get(String(companyId));
  }

  private createForm(): void {
    this.formGroup = new UntypedFormGroup({
      email: new UntypedFormControl({ value: this.currentMember?.user.email, disabled: this.isEditView } || null, [
        Validators.pattern(emailPattern),
        Validators.maxLength(70),
        Validators.required,
      ]),
      role: new UntypedFormControl(this.currentMember?.role.toString() || enumOrganizationRole.CONTRIBUTER.toString()),
      companies: new UntypedFormArray([
        ...this.companies.map((company) => {
          return new UntypedFormGroup({
            [company.id]: new UntypedFormControl(!!this.selectedCompaniesIds?.find((id) => id === company.id)),
          });
        }),
      ]),
    });
  }

  private getRoleNames(): Observable<{ id: string; label: string }[]> {
    const items: { id: string; label: string }[] = [
      {
        id: enumOrganizationRole.CONTRIBUTER.toString(),
        label: 'LABELS.MEMBER_ROLES.' + enumOrganizationRole[enumOrganizationRole.CONTRIBUTER],
      },
      {
        id: enumOrganizationRole.ADMIN.toString(),
        label: 'LABELS.MEMBER_ROLES.' + enumOrganizationRole[enumOrganizationRole.ADMIN],
      },
    ];

    return forkJoin(
      items.map((item) => {
        return this.translateService.get(item.label).pipe(
          map((modifiedLabel) => {
            return {
              ...item,
              label: modifiedLabel,
            };
          }),
        );
      }),
    );
  }
}
